import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  OPERATOR_LEGAL_NAME,
  OPERATOR_COMPANY_NUMBER,
  OPERATOR_REGISTERED_OFFICE,
  PRODUCT_NAME,
} from '@/legal/companyInfo';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-6 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-3xl space-y-8 px-6 py-10 text-sm leading-relaxed sm:text-base">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Privacy Policy</h1>
          <p className="mt-2 text-slate-600">Last updated: 9 May 2026</p>
        </div>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">1. Who is responsible for your information?</h2>
          <p>
            For the {PRODUCT_NAME} website and restaurant admin product, the data controller is{' '}
            <strong>{OPERATOR_LEGAL_NAME}</strong> (company number {OPERATOR_COMPANY_NUMBER}), registered office:{' '}
            {OPERATOR_REGISTERED_OFFICE}.
          </p>
          <p>
            When a restaurant uses {PRODUCT_NAME} to collect guest bookings, that restaurant is typically an
            independent controller of its customers&apos; personal data. This policy explains how we process data as
            operator of {PRODUCT_NAME}. Your restaurant should also provide its own privacy information to diners.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">2. What we collect</h2>
          <p>Depending on how you use {PRODUCT_NAME}, we may process:</p>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>Account and venue data</strong> — for example name, email, phone, address, billing-related
              identifiers, and configuration you enter in the admin console.
            </li>
            <li>
              <strong>Booking and operational data</strong> — reservation details you or your guests submit (names,
              contact details, party size, times, notes, deposit status, and similar fields required to run the
              service).
            </li>
            <li>
              <strong>Technical and security data</strong> — IP address, device/browser type, timestamps, and logs used
              to secure the service and diagnose faults.
            </li>
            <li>
              <strong>Communications</strong> — messages you send us for support and records we keep of those
              conversations.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">3. Why we use your data (lawful bases)</h2>
          <p>We use personal data to:</p>
          <ul className="list-disc space-y-1 pl-6">
            <li>perform our contract with you (provide, secure, and improve {PRODUCT_NAME});</li>
            <li>comply with legal obligations (for example tax or regulatory record-keeping where applicable);</li>
            <li>
              pursue legitimate interests — such as preventing fraud, monitoring reliability, and understanding
              aggregate usage — where those interests are not overridden by your rights.
            </li>
          </ul>
          <p>
            Where we rely on consent (for example certain marketing cookies if we add them), we will collect consent
            separately and you may withdraw it at any time.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">4. Cookies and similar technologies</h2>
          <p>
            We use cookies and local storage where needed for authentication (for example to keep you signed in to the
            admin console), preferences, and security. Strictly necessary cookies do not require consent under UK
            ePrivacy rules; analytics or advertising cookies, if introduced, would be described in a separate notice and
            where required we will ask for consent before setting them.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">5. Sharing and processors</h2>
          <p>We use reputable infrastructure and service providers (&quot;processors&quot;) to host and run {PRODUCT_NAME}. Categories of recipients may include:</p>
          <ul className="list-disc space-y-1 pl-6">
            <li>cloud hosting and database providers;</li>
            <li>email delivery services for transactional messages (for example confirmations and reminders);</li>
            <li>payment providers such as Stripe where deposit or card features are enabled;</li>
            <li>Google Calendar API where you connect a calendar;</li>
            <li>professional advisers where required (for example accountants or lawyers).</li>
          </ul>
          <p>
            Processors are bound by contract to protect your data and only process it on our instructions. Some
            providers may process data in countries outside the UK; where we transfer personal data internationally, we
            use appropriate safeguards such as the UK International Data Transfer Agreement or Addendum where required.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">6. Retention</h2>
          <p>
            We keep personal data only as long as needed for the purposes above, including legal, accounting, or dispute
            resolution requirements. Backup copies may persist for a limited period after deletion from live systems.
            Beta waitlist requests are kept until we process your application or delete them as part of data hygiene.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">7. Your rights (UK GDPR)</h2>
          <p>
            Subject to exemptions, you may have the right to access, rectify, erase, restrict processing, object to
            certain processing, and data portability in relation to personal data we hold about you as a {PRODUCT_NAME}{' '}
            account holder. You may also lodge a complaint with the UK Information Commissioner&apos;s Office (
            <a href="https://ico.org.uk" className="text-amber-800 underline hover:text-amber-950" target="_blank" rel="noopener noreferrer">
              ico.org.uk
            </a>
            ).
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">8. Security</h2>
          <p>
            We implement appropriate technical and organisational measures designed to protect personal data against
            accidental loss, unauthorised access, or disclosure. No internet transmission is completely secure; you
            should use strong passwords and protect your devices.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">9. Children</h2>
          <p>
            {PRODUCT_NAME} is a business service for restaurants and is not directed at children. Do not provide
            children&apos;s personal data except where legitimately needed for a booking (for example a highchair note)
            and in line with your own policies.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">10. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. The &quot;Last updated&quot; date at the top will change when
            we do. Material changes may also be communicated by email or in-product notice where appropriate.
          </p>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 text-slate-700">
          <h2 className="text-lg font-semibold text-slate-900">Contact</h2>
          <p className="mt-2">
            <strong>{OPERATOR_LEGAL_NAME}</strong>
            <br />
            Company number: {OPERATOR_COMPANY_NUMBER}
            <br />
            Registered office: {OPERATOR_REGISTERED_OFFICE}
          </p>
          <p className="mt-3 text-xs text-slate-500">
            For privacy requests relating to your {PRODUCT_NAME} account, contact us using the same channel you use for
            product support. This policy is a general description and not legal advice for your specific situation.
          </p>
        </section>
      </article>
    </div>
  );
}
